import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import Input from "../../components/Input/Input"
import Modal from "../../components/Modal/Modal"
import Tree, {
  TreeNode,
  BranchActionHandler,
  polimerTreeUpdateData,
} from "../../components/Tree/Tree"
import useStateGetter from "../../hooks/useStateGetter"
import { history } from "../../utils/routes"
import {
  fetchSortingOutputMaterials,
  createSortingOutputMaterial,
  updateSortingOutputMaterialTree,
  deleteSortingOutputMaterialTree,
} from "./actions"
import MaterialBranch from "../materials/components/MaterialBranch"
import {
  putSortingOutputMaterialTitle,
  showHideSortingOutputMaterial,
  setSelectableSortingOutputMaterial,
} from "./service/api"

const SortingOutputMaterials = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useDispatch()
  const materials = useStateGetter<TreeNode[]>(["sortingOutputMaterial", "sortingOutputMaterials"]) ?? []

  useEffect(() => {
    dispatch(fetchSortingOutputMaterials())
  }, [dispatch])

  const dragEndCallback = (
    dragedElement: number,
    targetElement: number,
    isIndentElement: boolean,
  ) => {
    const childrenIds = polimerTreeUpdateData(dragedElement)
    dispatch(updateSortingOutputMaterialTree({ dragedElement, targetElement, isIndentElement, childrenIds }))
  }

  const [title, setTitle] = useState("")
  const [parentId, setParentId] = useState(0)
  const [id, setId] = useState<null | number>(null)

  const handleAdd = async () => {
    if (title) {
      await dispatch(createSortingOutputMaterial({ title, parentId }))
      setParentId(0)
      setIsModalOpen(false)
    }
  }

  const handleUpdate = async () => {
    if (id && title) {
      await putSortingOutputMaterialTitle({ title, id })
      setParentId(0)
      setId(null)
      setTitle("")
      dispatch(fetchSortingOutputMaterials())
      setIsModalOpen(false)
    }
  }

  const handleBranchAction: BranchActionHandler = async ({ id, title }, actionName) => {
    switch (actionName) {
      case "visibility":
        await showHideSortingOutputMaterial({ id })
        dispatch(fetchSortingOutputMaterials())
        break
      case "selectable":
        await setSelectableSortingOutputMaterial({ id })
        dispatch(fetchSortingOutputMaterials())
        break
      case "add":
        setId(null)
        setTitle("")
        setParentId(id)
        setIsModalOpen(true)
        break
      case "edit":
        setId(id)
        setTitle(title)
        setIsModalOpen(true)
        break
      case "delete":
        dispatch(deleteSortingOutputMaterialTree({ id }))
        break
      default:
        break
    }
  }

  const { t } = useTranslation()

  return (
    <div className="srs-materials text-right">
      <div className="d-flex justify-content-between align-items-end">
        <h1 className="mb-5">{t("Manage 'sorting output'")}</h1>
        <button
          type="button"
          className="btn btn-success rounded-top-right mb-3"
          onClick={() => {
            setTitle("")
            setIsModalOpen(true)
          }}
        >
          {t("Add New Product")}
          <i className="fas fa-plus ml-3" />
        </button>
      </div>

      <Modal isOpen={isModalOpen} isOpenHandler={setIsModalOpen}>
        <div className="modal-header">
          <h5 className="modal-title">
            <strong>{id ? "Edit sorting output material" : "New sorting output material"}</strong>
          </h5>
        </div>
        <div className="modal-body">
          <Input
            label="Name"
            name="title"
            value={title}
            handleOnChange={(e) => setTitle(e.target.value)}
            inputWrapperClassName="col-sm-8"
            labelClassName="col-sm-4"
          />
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary rounded-bottom-left"
            onClick={() => setIsModalOpen(false)}
          >
            Close
          </button>
          <button
            type="button"
            disabled={!title}
            className="btn btn-success rounded-bottom-right"
            onClick={id ? handleUpdate : handleAdd}
          >
            Save
          </button>
        </div>
      </Modal>

      <div className="srs-tree-container bg-white border border-primary">
        <Tree
          nodes={materials}
          dragEndCallback={dragEndCallback}
          Branch={MaterialBranch}
          branchActionHandler={handleBranchAction}
          isDraggable
        />
      </div>

      <div className="d-flex justify-content-between mt-2">
        <button
          type="button"
          className="btn btn-outline-primary rounded-bottom-left"
          onClick={() => history.goBack()}
        >
          <i className="fas fa-arrow-left mr-2" /> Back
        </button>
      </div>
    </div>
  )
}

export default SortingOutputMaterials
